/**
 * useVideoTags Hook
 * Loads the organization's video label vocabulary and exposes creating and deleting a label.
 */

import { useCallback, useEffect, useState } from 'react';
import * as videoTagsService from '../services/video-tags';
import type { VideoTag } from '../types/prospect-media';
import { useAuth } from './useAuth';

export function useVideoTags() {
  const { effectiveOrganizationId } = useAuth();
  const [tags, setTags] = useState<VideoTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTags = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setTags(await videoTagsService.getVideoTags());
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load video tags';
      console.error('[useVideoTags] Failed to fetch video tags', err);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [effectiveOrganizationId]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  /**
   * Creates a label and folds it into the local list. Rethrows so the caller can surface
   * the reason — a duplicate name comes back as a 409 and the user needs to see that.
   */
  const createTag = useCallback(async (name: string): Promise<VideoTag> => {
    const created = await videoTagsService.createVideoTag(name);
    setTags((current) =>
      [...current, created].sort(
        (a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name)
      )
    );
    return created;
  }, []);

  /**
   * Deletes a label for the whole organization and drops it from the local list. The server
   * hides it on every video at once. Rethrows so the caller can say the delete failed.
   */
  const deleteTag = useCallback(async (tagId: string): Promise<void> => {
    await videoTagsService.deleteVideoTag(tagId);
    setTags((current) => current.filter((t) => t.id !== tagId));
  }, []);

  return { tags, loading, error, refresh: fetchTags, createTag, deleteTag };
}
